import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { getModels, getFans, getMessages, getPurchases } from '../api'
import { getRealmFromPath } from '../lib/realm'
import { Select, Pagination, PageSection, FilterGroup, FiltersRow } from '../components/ui'
import { useI18n } from '../i18n'
import '../styles/pages/models.css'
import '../styles/pages/page-common.css'
import '../styles/pages/model-data.css'

const localeForLang = (lang) => (lang === 'ru' ? 'ru-RU' : 'en-US')
const dateOptions = { dateStyle: 'short', timeStyle: 'short' }

const TYPES = {
  fans: {
    titleKey: 'modelData.fans.title',
    fetch: getFans,
    columns: [
      { key: 'model_email', labelKey: 'modelData.fans.model' },
      { key: 'username', labelKey: 'modelData.fans.username' },
      { key: 'name', labelKey: 'modelData.fans.name' },
      { key: 'external_id', labelKey: 'modelData.fans.id' },
      { key: 'is_test', labelKey: 'modelData.fans.test' },
      { key: 'created_at', labelKey: 'modelData.fans.createdAt' },
    ],
    getRow: (f, t, locale) => [f.model_email || t('common.notAvailable'), f.username || t('common.notAvailable'), f.name || t('common.notAvailable'), f.external_id, f.is_test ? t('chats.testBadge') : '—', f.created_at ? new Date(f.created_at).toLocaleString(locale, dateOptions) : t('common.notAvailable')],
  },
  chats: {
    titleKey: 'modelData.chats.title',
    fetch: getMessages,
    columns: [
      { key: 'model_email', labelKey: 'modelData.fans.model' },
      { key: 'direction', labelKey: 'modelData.chats.direction' },
      { key: 'fan_username', labelKey: 'modelData.chats.fan' },
      { key: 'external_id', labelKey: 'modelData.chats.messageId' },
      { key: 'created_at', labelKey: 'modelData.chats.createdAt' },
    ],
    getRow: (m, t, locale) => [m.model_email || t('common.notAvailable'), m.direction === 'in' ? t('modelData.chats.incoming') : t('modelData.chats.outgoing'), m.fan_username || t('common.notAvailable'), m.external_id, m.created_at ? new Date(m.created_at).toLocaleString(locale, dateOptions) : t('common.notAvailable')],
  },
  purchases: {
    titleKey: 'modelData.purchases.title',
    fetch: getPurchases,
    columns: [
      { key: 'model_email', labelKey: 'modelData.purchases.model' },
      { key: 'fan_username', labelKey: 'modelData.purchases.fan' },
      { key: 'purchase_type', labelKey: 'modelData.purchases.type' },
      { key: 'amount', labelKey: 'modelData.purchases.amount' },
      { key: 'is_test', labelKey: 'modelData.fans.test' },
      { key: 'created_at', labelKey: 'modelData.purchases.createdAt' },
    ],
    getRow: (p, t, locale) => [p.model_email || t('common.notAvailable'), p.fan_username || t('common.notAvailable'), p.purchase_type || t('common.notAvailable'), p.amount != null ? `${p.amount} ${p.currency || ''}` : t('common.notAvailable'), p.is_test ? t('chats.testBadge') : '—', p.created_at ? new Date(p.created_at).toLocaleString(locale, dateOptions) : t('common.notAvailable')],
  },
}

export default function ModelDataPage({ type }) {
  const { t, lang } = useI18n()
  const { pathname } = useLocation()
  const realm = getRealmFromPath(pathname)
  const canFilterTestReal = realm === 'admin' && (type === 'fans' || type === 'purchases')
  const routePrefix = pathname.startsWith('/v2') ? '/v2' : '/old'

  const locale = localeForLang(lang)
  const config = TYPES[type]
  const [models, setModels] = useState([])
  const [modelId, setModelId] = useState(null)
  const [isTestFilter, setIsTestFilter] = useState(null) // null = all (admin), true/false
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingModels, setLoadingModels] = useState(true)
  const [page, setPage] = useState(0)
  const limit = 20

  useEffect(() => {
    setPage(0)
  }, [modelId, config, canFilterTestReal, isTestFilter])

  useEffect(() => {
    getModels()
      .then((r) => {
        const list = Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : [])
        setModels(list)
      })
      .catch(() => setModels([]))
      .finally(() => setLoadingModels(false))
  }, [])

  useEffect(() => {
    if (!config) return
    setLoading(true)
    const opts = { modelId: modelId || undefined, limit, offset: page * limit }
    if (canFilterTestReal && isTestFilter != null) opts.isTest = isTestFilter
    config.fetch(opts)
      .then((r) => {
        setItems(Array.isArray(r?.data) ? r.data : [])
        setTotal(r?.total ?? 0)
      })
      .catch(() => { setItems([]); setTotal(0) })
      .finally(() => setLoading(false))
  }, [modelId, config, canFilterTestReal, isTestFilter, page])

  if (!config) return null

  const options = models.length
    ? [{ value: '', label: t('modelData.allModels') }, ...models.map((m) => ({ value: String(m.id), label: m.of_email || m.name || t('common.notAvailable') }))]
    : [{ value: '', label: t('modelData.selectModel') }]

  return (
    <PageSection
      className="model-data-page"
      title={t(config.titleKey)}
    >
      <>
        <FiltersRow>
          {canFilterTestReal && (
            <FilterGroup label={t('chats.filter.type')}>
              <Select
                className="filter-select"
                value={isTestFilter === null ? '' : String(isTestFilter)}
                onChange={(v) => setIsTestFilter(v === '' ? null : v === 'true')}
                options={[
                  { value: '', label: t('modelData.allTypes') },
                  { value: 'false', label: t('chats.tab.real') },
                  { value: 'true', label: t('chats.tab.test') },
                ]}
                placeholder={t('modelData.allTypes')}
                dropdownPosition="down"
              />
            </FilterGroup>
          )}
          <FilterGroup label={t('modelData.filterByModel')}>
            <Select
              className="filter-select"
              value={modelId != null ? String(modelId) : ''}
              placeholder={t('modelData.filterByModel')}
              options={options}
              onChange={(v) => setModelId(v ? Number(v) : null)}
              dropdownPosition="down"
            />
            {modelId && (
              <Link to={`${routePrefix}/models/${modelId}`} className="filter-link">
                {t('modelData.toModel')}
              </Link>
            )}
          </FilterGroup>
        </FiltersRow>
        {loadingModels ? (
        <p className="model-data-loading">{t('modelData.loadingModels')}</p>
      ) : loading ? (
        <p className="model-data-loading">{t('modelData.loading')}</p>
      ) : (
        <div className="model-data-table-wrap">
          <span className="model-data-total">{t('modelData.total', { count: total })}</span>
          {items.length ? (
            <>
              <table className="data-table" aria-label={t(config.titleKey)}>
                <thead>
                  <tr>
                    {config.columns.map((col) => (
                      <th key={col.key} scope="col">
                        {t(col.labelKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      {config.getRow(item, t, locale).map((cell, i) => (
                        <td key={i}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {total > 0 && (
                <Pagination
                  page={page}
                  total={total}
                  limit={limit}
                  onPageChange={setPage}
                  loading={loading}
                  t={t}
                />
              )}
            </>
          ) : (
            <p className="model-data-empty">{t('modelData.noData')}</p>
          )}
        </div>
      )}
      </>
    </PageSection>
  )
}
